import { EPath } from "constants/path";
import React from "react";

import {
  LazyActivityPage,
  LazyAddPatientPage,
  LazyAppointmentPage,
  LazyAppointmentSettingsPage,
  LazyBodyMeasurementsPage,
  LazyChatPage,
  LazyDailyWellBeingScorePage,
  LazyGeneratedProtocolResultPage,
  LazyHeartMetricsPage,
  LazyHomePage,
  LazyInviteProviderPage,
  LazyLearningCertificationPage,
  LazyNutritionAndHydrationPage,
  LazyOnboardingPage,
  LazyPatientDashboard,
  LazyPatientManagementPage,
  LazyPaymentManagementPage,
  LazyAddServicePage,
  LazyPracticeConfigurationPage,
  LazyPracticeDetailPage,
  LazyPracticeManagementPage,
  LazyPracticeProfilePage,
  LazyPrescriptionCreatePage,
  LazyPrescriptionDetailPage,
  LazyPrescriptionManagementPage,
  LazyPrescriptionsPage,
  LazyReproductiveHealthPage,
  LazyRoleManagementPage,
  LazySettingsResetPasswordPage,
  LazySleepPage,
  LazyTreatmentPlanPage,
  LazyUnauthorizedPage,
  LazyUserProfilePage,
  LazyWearablesPage,
  LazyPaymentSuccessfulPage,
  LazyInstructionGenerationPage,
} from "./lazyPages";

export interface RouteConfig {
  path: string;
  element: React.ComponentType<Record<string, unknown>>;
  protected?: boolean;
  requireOnboarding?: boolean;
  title?: string;
  description?: string;
}

const routeConfig: RouteConfig[] = [
  {
    path: EPath.HOME,
    element: LazyHomePage,
    protected: true,
    title: "Dashboard",
    description: "Main dashboard",
  },
  //practice management
  {
    path: EPath.PRACTICE_MANAGEMENT,
    element: LazyPracticeManagementPage,
    protected: true,
    title: "Practice Management",
    description: "Manage staff and settings",
  },
  //patient dashboard
  {
    path: EPath.PATIENT_DASHBOARD,
    element: LazyPatientDashboard,
    protected: true,
    title: "Patient Dashboard",
    description: "Main patient dashboard",
  },
  //patient management
  {
    path: EPath.PATIENT_MANAGEMENT,
    element: LazyPatientManagementPage,
    protected: true,
    title: "Patient Management",
    description: "Manage patient profiles",
  },
  //add patient
  {
    path: EPath.ADD_PATIENT,
    element: LazyAddPatientPage,
    protected: true,
    title: "Add Patient",
    description: "Create a new patient profile",
  },
  //appointment calendar
  {
    path: EPath.APPOINTMENT,
    element: LazyAppointmentPage,
    protected: true,
    title: "Appointment",
    description: "Appointment calendar and scheduling",
  },
  //appointment settings
  {
    path: EPath.APPOINTMENT_SETTINGS,
    element: LazyAppointmentSettingsPage,
    protected: true,
    title: "Appointment Settings",
    description: "Manage appointment types and settings",
  },
  //treatment plan
  {
    path: EPath.TREATMENT_PLAN,
    element: LazyTreatmentPlanPage,
    protected: true,
    title: "Treatment Plan",
    description: "Manage treatment plan for patients",
  },
  //personalized wellness protocol
  {
    path: EPath.PERSONALIZED_WELLNESS_PROTOCOL,
    element: LazyTreatmentPlanPage,
    protected: true,
    title: "Personalized Wellness Protocol",
    description: "View and manage personalized wellness protocol",
  },
  //generated protocol result
  {
    path: "/treatment-plan/generated-result/:patientId/:treatmentId",
    element: LazyGeneratedProtocolResultPage,
    protected: true,
    title: "Generated Protocol Result",
    description: "View details of generated treatment protocol",
  },
  //practice profile
  {
    path: EPath.PRACTICE_PROFILE,
    element: LazyPracticeProfilePage,
    protected: true,
    title: "Practice Profile",
    description: "View your practice profile details",
  },
  //practice detail
  {
    path: EPath.PRACTICE_DETAIL,
    element: LazyPracticeDetailPage,
    protected: true,
    title: "Practice Details",
    description: "View practice profile details",
  },
  //invite practice
  {
    path: EPath.INVITE_PRACTICE,
    element: LazyInviteProviderPage,
    protected: true,
    title: "Invite Practice",
    description: "Create a new practice",
  },
  //payment management
  {
    path: EPath.PAYMENT_MANAGEMENT,
    element: LazyPaymentManagementPage,
    protected: true,
    title: "Payment Management",
    description: "Manage payments and billing",
  },
  //add service
  {
    path: EPath.ADD_SERVICE,
    element: LazyAddServicePage,
    protected: true,
    title: "Add Service",
    description: "Purchase additional services and modalities",
  },
  //practice configuration
  {
    path: EPath.PRACTICE_CONFIGURATION,
    element: LazyPracticeConfigurationPage,
    protected: true,
    title: "Practice Configuration",
    description: "Manage practice configuration and payout settings",
  },
  //prescription management
  {
    path: EPath.PRESCRIPTION_MANAGEMENT,
    element: LazyPrescriptionManagementPage,
    protected: true,
    title: "Prescription Management",
    description: "Manage prescriptions",
  },
  //prescription Details
  {
    path: EPath.PRESCRIPTION_DETAIL,
    element: LazyPrescriptionDetailPage,
    protected: true,
    title: "Prescription Details",
    description: "View prescription details",
  },
  //prescription Create
  {
    path: EPath.PRESCRIPTION_CREATE,
    element: LazyPrescriptionCreatePage,
    protected: true,
    title: "Add New Prescription",
    description: "Create a new prescription",
  },
  //onboarding
  {
    path: EPath.ONBOARDING,
    element: LazyOnboardingPage,
    protected: true,
    requireOnboarding: true,
    title: "Onboarding",
    description: "Complete your profile setup",
  },
  //user profile
  {
    path: EPath.PROFILE,
    element: LazyUserProfilePage,
    protected: true,
    title: "My Profile",
    description: "View and edit your profile information",
  },
  //settings reset password
  {
    path: EPath.SETTINGS_RESET_PASSWORD,
    element: LazySettingsResetPasswordPage,
    protected: true,
    title: "Reset Password",
    description: "Update your password",
  },
  //unauthorized access
  {
    path: EPath.UNAUTHORIZED,
    element: LazyUnauthorizedPage,
    protected: true,
    title: "Access Denied",
    description: "Unauthorized access to restricted feature",
  },
  //nutrition and hydration
  {
    path: EPath.NUTRITION_HYDRATION,
    element: LazyNutritionAndHydrationPage,
    protected: true,
    title: "Nutrition and Hydration",
    description: "View detailed nutrition and hydration data",
  },
  //daily well being score
  {
    path: EPath.DAILY_WELL_BEING_SCORE,
    element: LazyDailyWellBeingScorePage,
    protected: true,
    title: "Daily Well Being Score",
    description: "View detailed daily well being score data",
  },
  {
    path: EPath.REPRODUCTIVE_HEALTH,
    element: LazyReproductiveHealthPage,
    title: "Reproductive Health",
    description: "View detailed reproductive health data",
    protected: true,
  },
  // activity
  {
    path: EPath.ACTIVITY,
    element: LazyActivityPage,
    protected: true,
    title: "Activity",
    description: "View detailed activity data",
  },
  // sleep
  {
    path: EPath.SLEEP,
    element: LazySleepPage,
    protected: true,
    title: "Sleep",
    description: "View detailed sleep data",
  },
  // heart metrics
  {
    path: EPath.HEART_METRICS,
    element: LazyHeartMetricsPage,
    protected: true,
    title: "Vital Signs",
    description: "View detailed vital signs data",
  },
  // role management
  {
    path: EPath.ROLE_MANAGEMENT,
    element: LazyRoleManagementPage,
    protected: true,
    title: "Role Management",
    description: "Manage roles and permissions",
  },
  //body measurements
  {
    path: EPath.BODY_MEASUREMENTS,
    element: LazyBodyMeasurementsPage,
    protected: true,
    title: "Body Measurements",
    description: "View detailed body measurements data",
  },
  // wearables
  {
    path: EPath.WEARABLES,
    element: LazyWearablesPage,
    protected: true,
    title: "Wearables",
    description: "View all wearable device data",
  },
  // prescriptions
  {
    path: EPath.PRESCRIPTIONS,
    element: LazyPrescriptionsPage,
    protected: true,
    title: "Prescriptions / Rx",
    description: "View patient prescriptions and medications",
  },
  // chat
  {
    path: EPath.COMMUNICATIONS,
    element: LazyChatPage,
    protected: true,
    title: "Communications",
    description: "View detailed communications data",
  },
  // learning certification
  {
    path: EPath.LEARNING_CERTIFICATION,
    element: LazyLearningCertificationPage,
    protected: true,
    title: "Provider Learning & Certification",
    description: "Access learning resources and certification programs",
  },
  // payment successful
  {
    path: EPath.PAYMENT_SUCCESSFUL,
    element: LazyPaymentSuccessfulPage,
    protected: true,
    title: "Payment Successful",
    description: "Payment successful",
  },
  // instruction generation
  {
    path: EPath.INSTRUCTION_GENERATION,
    element: LazyInstructionGenerationPage,
    protected: true,
    title: "Instruction Generation",
    description: "View detailed instruction generation data",
  },
];
export default routeConfig;
